import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const WellnessCSVImport = ({ onImport, onClose }) => {
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const fileInputRef = useRef(null);

  const expectedColumns = ['date', 'sleep', 'fatigue', 'soreness', 'stress', 'notes'];
  
  const sampleCSV = `date,sleep,fatigue,soreness,stress,notes
12/11/2025,6,3,2,2,Good recovery day after training
11/11/2025,5,4,4,3,Feeling the effects of yesterday's session
10/11/2025,7,2,1,1,Excellent day well rested`;

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFile(e?.dataTransfer?.files?.[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFile(e?.target?.files?.[0]);
    }
  };

  const handleFile = (selectedFile) => {
    if (selectedFile?.type !== 'text/csv' && !selectedFile?.name?.endsWith('.csv')) {
      setValidationResults({
        isValid: false,
        errors: ['Please select a CSV file'],
        warnings: []
      });
      return;
    }

    setFile(selectedFile);
    processFile(selectedFile);
  };

  const processFile = (file) => {
    setIsProcessing(true);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e?.target?.result;
        const lines = csv?.split('\n')?.filter(line => line?.trim());
        
        if (lines?.length < 2) {
          setValidationResults({
            isValid: false,
            errors: ['CSV file must contain at least a header row and one data row'],
            warnings: []
          });
          setIsProcessing(false);
          return;
        }

        const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase());
        const dataRows = lines?.slice(1);
        
        const validation = validateCSV(headers, dataRows);
        setValidationResults(validation);
        
        if (validation?.isValid) {
          const parsedData = parseCSVData(headers, dataRows);
          setPreviewData(parsedData?.slice(0, 5)); // Show first 5 rows for preview
        }
        
      } catch (error) {
        setValidationResults({
          isValid: false,
          errors: ['Error reading CSV file. Please check the file format.'],
          warnings: []
        });
      }
      
      setIsProcessing(false);
    };
    
    reader?.readAsText(file);
  };

  const validateCSV = (headers, dataRows) => {
    const errors = [];
    const warnings = [];
    
    // Check required columns (notes is optional)
    const requiredColumns = ['date', 'sleep', 'fatigue', 'soreness', 'stress'];
    const missingColumns = requiredColumns?.filter(col => !headers?.includes(col));
    if (missingColumns?.length > 0) {
      errors?.push(`Missing required columns: ${missingColumns?.join(', ')}`);
    }
    
    // Validate data rows
    let validRowCount = 0;
    dataRows?.forEach((row, index) => {
      const values = row?.split(',')?.map(v => v?.trim());
      const rowNumber = index + 2; // +2 because index starts at 0 and we skip header
      
      if (values?.length !== headers?.length) {
        warnings?.push(`Row ${rowNumber}: Column count mismatch`);
        return;
      }
      
      // Validate date
      const dateIndex = headers?.indexOf('date');
      if (dateIndex >= 0) {
        const dateValue = values?.[dateIndex];
        if (!isValidDate(dateValue)) {
          warnings?.push(`Row ${rowNumber}: Invalid date format (use DD/MM/YYYY)`);
          return;
        }
      }
      
      // Validate wellness scores (1-7 scale)
      const wellnessFields = ['sleep', 'fatigue', 'soreness', 'stress'];
      let hasValidationError = false;
      
      wellnessFields?.forEach(field => {
        const fieldIndex = headers?.indexOf(field);
        if (fieldIndex >= 0) {
          const fieldValue = parseInt(values?.[fieldIndex]);
          if (isNaN(fieldValue) || fieldValue < 1 || fieldValue > 7) {
            warnings?.push(`Row ${rowNumber}: ${field} must be between 1 and 7`);
            hasValidationError = true;
          }
        }
      });
      
      if (hasValidationError) return;
      
      validRowCount++;
    });
    
    if (validRowCount === 0) {
      errors?.push('No valid data rows found');
    }
    
    return {
      isValid: errors?.length === 0,
      errors,
      warnings,
      validRowCount
    };
  };

  const isValidDate = (dateString) => {
    // Accept DD/MM/YYYY format
    const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = dateString?.match(dateRegex);
    
    if (!match) return false;
    
    const day = parseInt(match?.[1]);
    const month = parseInt(match?.[2]);
    const year = parseInt(match?.[3]);
    
    const date = new Date(year, month - 1, day);
    return date?.getFullYear() === year && 
           date?.getMonth() === month - 1 && 
           date?.getDate() === day;
  };

  const parseCSVData = (headers, dataRows) => {
    return dataRows
      ?.filter((row, index) => {
        const values = row?.split(',')?.map(v => v?.trim());
        const rowNumber = index + 2;
        
        // Basic validation to filter out invalid rows
        const dateIndex = headers?.indexOf('date');
        const sleepIndex = headers?.indexOf('sleep');
        const fatigueIndex = headers?.indexOf('fatigue');
        const sorenessIndex = headers?.indexOf('soreness');
        const stressIndex = headers?.indexOf('stress');
        
        if (dateIndex >= 0 && !isValidDate(values?.[dateIndex])) return false;
        if (sleepIndex >= 0) {
          const sleep = parseInt(values?.[sleepIndex]);
          if (isNaN(sleep) || sleep < 1 || sleep > 7) return false;
        }
        if (fatigueIndex >= 0) {
          const fatigue = parseInt(values?.[fatigueIndex]);
          if (isNaN(fatigue) || fatigue < 1 || fatigue > 7) return false;
        }
        if (sorenessIndex >= 0) {
          const soreness = parseInt(values?.[sorenessIndex]);
          if (isNaN(soreness) || soreness < 1 || soreness > 7) return false;
        }
        if (stressIndex >= 0) {
          const stress = parseInt(values?.[stressIndex]);
          if (isNaN(stress) || stress < 1 || stress > 7) return false;
        }
        
        return true;
      })
      ?.map((row, index) => {
        const values = row?.split(',')?.map(v => v?.trim());
        const wellnessData = {};
        
        headers?.forEach((header, i) => {
          wellnessData[header] = values?.[i];
        });
        
        // Convert date format from DD/MM/YYYY to YYYY-MM-DD
        if (wellnessData?.date) {
          const [day, month, year] = wellnessData?.date?.split('/');
          wellnessData.date = `${year}-${month?.padStart(2, '0')}-${day?.padStart(2, '0')}`;
        }
        
        // Convert numeric fields
        wellnessData.sleep = parseInt(wellnessData?.sleep);
        wellnessData.fatigue = parseInt(wellnessData?.fatigue);
        wellnessData.soreness = parseInt(wellnessData?.soreness);
        wellnessData.stress = parseInt(wellnessData?.stress);
        wellnessData.hooperIndex = wellnessData?.sleep + wellnessData?.fatigue + wellnessData?.soreness + wellnessData?.stress;
        wellnessData.id = `import-${Date.now()}-${index}`;
        
        return wellnessData;
      });
  };

  const handleImport = () => {
    if (!file || !validationResults?.isValid) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const csv = e?.target?.result;
      const lines = csv?.split('\n')?.filter(line => line?.trim());
      const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase());
      const dataRows = lines?.slice(1);
      
      const parsedData = parseCSVData(headers, dataRows);
      onImport(parsedData);
      onClose();
    };
    
    reader?.readAsText(file);
  };

  const downloadSample = () => {
    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample-wellness.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Upload" size={18} color="white" strokeWidth={2} />
              </div>
              <h2 className="text-xl font-semibold text-text-primary">Import Wellness Data from CSV</h2>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} color="currentColor" strokeWidth={2} />
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Sample Download */}
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-text-primary mb-1">Need a template?</h3>
                <p className="text-xs text-text-secondary">
                  Download a sample CSV file with the correct format
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={downloadSample}>
                <Icon name="Download" size={16} color="currentColor" strokeWidth={2} />
                Sample CSV
              </Button>
            </div>
          </div>

          {/* File Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-150 ${
              dragActive 
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            <div className="space-y-4">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
                <Icon name="FileText" size={24} color="var(--color-text-secondary)" strokeWidth={2} />
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-text-primary mb-2">
                  {file ? file?.name : 'Drop your CSV file here'}
                </h3>
                <p className="text-text-secondary mb-4">
                  {file ? 'File selected successfully' : 'or click to browse files'}
                </p>
                
                {!file && (
                  <Button 
                    variant="outline" 
                    onClick={() => fileInputRef?.current?.click()}
                  >
                    Choose File
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Processing Indicator */}
          {isProcessing && (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="text-text-secondary">Processing file...</span>
              </div>
            </div>
          )}

          {/* Validation Results */}
          {validationResults && !isProcessing && (
            <div className="space-y-4">
              {validationResults?.errors?.length > 0 && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="AlertCircle" size={20} color="var(--color-error)" strokeWidth={2} />
                    <div>
                      <h4 className="text-sm font-medium text-error mb-2">Validation Errors</h4>
                      <ul className="text-xs text-text-secondary space-y-1">
                        {validationResults?.errors?.map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {validationResults?.warnings?.length > 0 && (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="AlertTriangle" size={20} color="var(--color-warning)" strokeWidth={2} />
                    <div>
                      <h4 className="text-sm font-medium text-warning mb-2">Warnings</h4>
                      <ul className="text-xs text-text-secondary space-y-1">
                        {validationResults?.warnings?.map((warning, index) => (
                          <li key={index}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {validationResults?.isValid && (
                <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={20} color="var(--color-success)" strokeWidth={2} />
                    <div>
                      <h4 className="text-sm font-medium text-success mb-1">File Valid</h4>
                      <p className="text-xs text-text-secondary">
                        Ready to import {validationResults?.validRowCount} wellness entries
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Preview Data */}
          {previewData?.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-text-primary mb-3">Preview (First 5 rows)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Sleep</th>
                      <th className="px-3 py-2 text-left">Fatigue</th>
                      <th className="px-3 py-2 text-left">Soreness</th>
                      <th className="px-3 py-2 text-left">Stress</th>
                      <th className="px-3 py-2 text-left">Hooper Index</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {previewData?.map((row, index) => (
                      <tr key={index}>
                        <td className="px-3 py-2">{new Date(row.date)?.toLocaleDateString('en-GB')}</td>
                        <td className="px-3 py-2">{row?.sleep}</td>
                        <td className="px-3 py-2">{row?.fatigue}</td>
                        <td className="px-3 py-2">{row?.soreness}</td>
                        <td className="px-3 py-2">{row?.stress}</td>
                        <td className="px-3 py-2">{row?.hooperIndex}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-xs text-text-secondary">
              Expected format: date (DD/MM/YYYY), sleep (1-7), fatigue (1-7), soreness (1-7), stress (1-7), notes
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleImport}
                disabled={!validationResults?.isValid}
                iconName="Upload"
                iconPosition="left"
              >
                Import Wellness Data
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellnessCSVImport;